import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
	Table,
	Button,
	Modal,
	Form,
	Input,
	Row,
	Col,
	Card,
	Select,
	Tag,
	notification,
	Space,
} from "antd";
import {
	SafetyCertificateOutlined,
	EyeOutlined,
	MessageOutlined,
	EditOutlined,
} from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import qaService from "../../services/qualityAssurance";
import { useMediaQuery } from "react-responsive";
import { Excel } from "antd-table-saveas-excel";
import { useSelector } from "react-redux";
import TableActionBtnsComponent from "../../components/tableActionsBtns";
import PageMeta from "../../config/pageMeta";

const { Option } = Select;

const QualityAssurance = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form] = Form.useForm();
	const [formStatus] = Form.useForm();
	const [currentPage, setCurrentPage] = useState(1);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [isModalStatusVisible, setIsModalStatusVisible] = useState(false);
	const { currentUser } = useSelector((state) => state.user);
	const [formId, setFormId] = useState(1);
	const meta = PageMeta.qualityAssuranceForm;

	const allowedPermissions = currentUser?.["roles.permission"]?.map(
		(permission) => permission.name
	);

	const hasPermission = (permission) =>
		allowedPermissions?.includes(permission);

	const handleStatus = (record) => {
		setIsModalStatusVisible(true);
		formStatus.setFieldsValue({
			id: record.id,
		});
	};

	const onStatusFinish = async (values) => {
		setLoading(true);
		try {
			const response = await qaService.updateQAStatus(values);
			setIsModalStatusVisible(false);
			setEditing(null);
			form.resetFields();
			notification.success({
				message: "Quality Assurance Updated",
				description: response.message,
			});
			getQA(currentPage); // Refresh current page after update
		} catch (error) {
			console.log(error);
			notification.error({
				message: "Error",
				description: error.response?.data?.message || "An error occurred",
			});
		} finally {
			setLoading(false);
		}
	};

	const getQA = async (page = 1, pageSize = 10) => {
		setLoading(true);
		try {
			const response = await qaService.getQA(page, pageSize);
			setData(response.data);
			setCurrentPage(response.meta.current_page);
			setTotalDataCount(response.meta.total);
			setItemsPerPage(response.meta.per_page);
		} catch (error) {
			console.error("Error fetching QA data:", error);
			notification.error({
				message: "Error",
				description: "Failed to fetch quality assurance data",
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getQA();
	}, []);

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalStatusVisible(false);
		setIsModalVisible(false);
		setEditing(null);
		form.resetFields();
	};

	const handleClick = () => {
		const excelColumns = columns
			.filter((column) => column.key !== "Actions")
			.map((column) => {
				const { render, ...columnWithoutRender } = column;
				return columnWithoutRender;
			});

		const excel = new Excel();
		excel
			.addSheet("data")
			.addColumns(excelColumns)
			.addDataSource(data, {
				str2Percent: true,
			})
			.saveAs("Quality_Assurance.xlsx");
	};

	const handleTableChange = (pagination, filters, sorter) => {
		getQA(pagination.current, pagination.pageSize);
	};

	const columns = [
		{
			title: "Request Title",
			dataIndex: "request_title",
			key: "request_title",
			width: 200,
			fixed: useMediaQuery({ minWidth: 768 }) ? "left" : null,
			sorter: (a, b) => a.request_title.localeCompare(b.request_title),
			render: (text) => (text?.length > 40 ? `${text.slice(0, 40)}...` : text),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 200,
			sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
		},
		{
			title: "Department",
			dataIndex: ["department", "name"],
			key: "department",
			width: 200,
			sorter: (a, b) =>
				a.department?.name?.localeCompare(b.department?.name || ""),
		},
		{
			title: "Location",
			dataIndex: ["location", "name"],
			key: "location",
			width: 200,
			sorter: (a, b) => a.location?.name?.localeCompare(b.location?.name || ""),
		},
		{
			title: "Designation",
			dataIndex: ["designation", "name"],
			key: "designation",
			width: 200,
			sorter: (a, b) =>
				a.designation?.name?.localeCompare(b.designation?.name || ""),
		},
		{
			title: "Section",
			dataIndex: ["section", "name"],
			key: "section",
			width: 200,
			sorter: (a, b) => a.section?.name?.localeCompare(b.section?.name || ""),
		},
		{
			title: "Created At",
			dataIndex: "created_at",
			key: "created_at",
			width: 200,
			sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
		},
		{
			title: "Actions",
			key: "Actions",
			fixed: useMediaQuery({ minWidth: 768 }) ? "right" : null,
			width: 80,
			render: (text, record) => (
				<TableActionBtnsComponent
					record={record}
					currentUser={currentUser}
					formId={formId}
				/>
			),
		},
	];

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header
						icon={meta.icon}
						title={meta.title}
						right={
							<>
								{hasPermission("OwnWorkflow-view") && (
									<Link to={`/own-workflow?form_id=1`}>
										<Button className='mr-10'>My Workflows</Button>
									</Link>
								)}
								<Button onClick={handleClick} className='mr-10'>
									Export
								</Button>
							</>
						}
					/>
				</Col>
			</Row>

			<Card>
				<Table
					scroll={{ x: 1200 }}
					sticky={true}
					style={{ minHeight: "100vh" }}
					columns={columns}
					dataSource={data.map((item) => ({ ...item, key: item.id }))}
					rowKey={(record) => record.id}
					onChange={handleTableChange}
					pagination={{
						total: totalDataCount,
						current: currentPage,
						// pageSize: itemsPerPage,
						// showSizeChanger: true,
						// pageSizeOptions: ["10", "20", "50", "100"],
						// showTotal: (total, range) =>
						// 	`${range[0]}-${range[1]} of ${total} items`,
					}}
					loading={loading}
				/>
			</Card>

			<Modal
				title='Quality Assurance Status'
				open={isModalStatusVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={formStatus} onFinish={onStatusFinish} layout='vertical'>
					<Form.Item name='id' hidden>
						<Input />
					</Form.Item>

					<Form.Item
						name='status'
						label='Status'
						rules={[{ required: true, message: "Please select a status" }]}
					>
						<Select placeholder='Select status'>
							<Option value='Ok'>OK</Option>
							<Option value='Modification Required'>
								Modification Required
							</Option>
						</Select>
					</Form.Item>
					<Form.Item
						name='feedback'
						label='Feedback'
						rules={[{ required: true, message: "Please provide feedback" }]}
					>
						<Input.TextArea rows={4} placeholder='Provide feedback' />
					</Form.Item>
					<Form.Item>
						<Button
							type='primary'
							htmlType='submit'
							loading={loading}
							className='btn-blue mr-20'
						>
							Update Status
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</DefaultLayout>
	);
};

export default QualityAssurance;
